import type { MetaFunction, LoaderFunctionArgs } from 'react-router'
import { redirect } from 'react-router'
import { SEO_PAGE_HEADERS } from "~/lib/cache.server"
import { useLoaderData, Link } from 'react-router'
import { db } from '@afficheservices/db'
import {
  serviceCategories, professionalProfiles, professionalCategories, geoFrance,
} from '@afficheservices/db/schema'
import { eq, and, sql, desc } from 'drizzle-orm'
import { assertCityNotDomTom } from '~/lib/dom-tom.server'
import { Navbar } from '~/components/navbar'
import { Footer } from '~/components/footer'
import { ProMap } from '~/components/pro-map'
import { FAQSchema, ServiceSchema, BreadcrumbSchema, CityPlaceSchema, ItemListSchema, geoMeta } from '~/components/structured-data'
import { InlineQuiz } from '~/components/inline-quiz'
import { getSeoContent } from '~/lib/seo-content.server'
import { ContentMeta, SourceCitations, InternaunteQuestions } from '~/components/content-trust'
import { generateCityServiceContent, fetchCityContext } from '~/lib/city-content'
import { artisanSlug, SERVICE_LABELS } from '~/lib/programmatic-seo'
import { TableOfContents, ArticleMeta, PageStats, ArticleSchema, HowToSchema, ComparisonTable } from '~/components/seo-content'
import { DemandCounter } from '~/components/demand-counter'
import { formatCityName } from '~/lib/city-format'
import { CityPriceComparison } from '~/components/city-price-comparison'
import { ProCard } from '~/components/pro-card'
import { FloatingCTA } from '~/components/floating-cta'
import { wikidataUrlForInsee, communeImageForInsee } from '~/lib/commune-wikidata.server'
import { CheckCircle, ArrowRight, MapPin,
} from 'lucide-react'

// ─── Constants ───────────────────────────────────────────────

const PAGE_SIZE = 15

// Service-specific FAQs for rich snippets
const SERVICE_FAQS: Record<string, Array<{ question: string; answer: string }>> = {
  plomberie: [
    { question: 'Combien coûte un plombier ?', answer: 'Le tarif d\'un plombier varie de 80 € à 500 € selon le type d\'intervention. Un dépannage simple coûte entre 80 € et 200 €, tandis qu\'une installation complète peut atteindre 500 € à 1200 €.' },
    { question: 'Comment trouver un bon plombier ?', answer: 'Vérifiez les avis clients, l\'assurance RC Pro, et le numéro SIRET. Sur AfficheServices, tous les plombiers sont vérifiés et notés par de vrais clients.' },
    { question: 'Un plombier peut-il intervenir en urgence ?', answer: 'Oui, de nombreux plombiers proposent des interventions d\'urgence 24h/24. Attention, les interventions de nuit et week-end coûtent 50 à 100% plus cher.' },
    { question: 'Faut-il un devis avant intervention ?', answer: 'Oui, un devis écrit est obligatoire avant tout travail dépassant 150 €. Sur AfficheServices, vous recevez jusqu\'à 3 devis gratuits pour comparer.' },
  ],
  electricite: [
    { question: 'Combien coûte un électricien ?', answer: 'Les tarifs d\'un électricien varient de 150 € à 1500 €. Une installation de prises coûte 80-150 €/point, une mise aux normes complète de 1000 € à 3000 €.' },
    { question: 'La mise aux normes électrique est-elle obligatoire ?', answer: 'La norme NF C 15-100 est obligatoire pour les constructions neuves et les rénovations importantes. Un diagnostic électrique est requis pour la vente d\'un logement de plus de 15 ans.' },
    { question: 'Comment vérifier qu\'un électricien est qualifié ?', answer: 'Vérifiez la certification Qualifelec ou la mention RGE. L\'électricien doit fournir une attestation de conformité CONSUEL après les travaux.' },
  ],
  peinture: [
    { question: 'Quel est le prix de la peinture au m² ?', answer: 'Le prix moyen de la peinture est de 20 à 45 €/m² pour l\'intérieur (murs), 25 à 50 €/m² pour le plafond, et 30 à 60 €/m² pour la façade extérieure.' },
    { question: 'Combien de temps pour peindre un appartement ?', answer: 'Comptez 3 à 5 jours pour un T3 (70m²) en peinture standard. Les travaux incluent la préparation des surfaces, 2 couches de peinture et le nettoyage.' },
    { question: 'Quelle peinture choisir ?', answer: 'Pour l\'intérieur, la peinture acrylique (à l\'eau) est recommandée : peu d\'odeur, séchage rapide, nettoyage facile. Pour les pièces humides, optez pour une peinture anti-humidité.' },
  ],
  renovation: [
    { question: 'Combien coûte une rénovation complète ?', answer: 'Le coût varie selon l\'ampleur : rafraîchissement 200-400 €/m², rénovation moyenne 400-800 €/m², rénovation lourde 800-1500 €/m². Un T3 de 70m² coûte entre 14000 € et 56000 €.' },
    { question: 'Quelles aides pour la rénovation ?', answer: 'MaPrimeRénov\', l\'éco-PTZ, les CEE (certificats d\'économies d\'énergie) et la TVA réduite à 5.5% pour les travaux d\'amélioration énergétique.' },
    { question: 'Par où commencer une rénovation ?', answer: 'Commencez par un diagnostic (structure, électricité, plomberie), puis planifiez dans l\'ordre : gros œuvre, isolation, plomberie/électricité, revêtements, finitions.' },
  ],
}

// Default FAQs for categories without specific ones
const DEFAULT_FAQS = [
  { question: 'Comment obtenir un devis gratuit ?', answer: 'Décrivez votre projet en 2 minutes sur AfficheServices. Vous recevrez jusqu\'à 3 devis gratuits de professionnels vérifiés sous 48h.' },
  { question: 'Les artisans sont-ils vérifiés ?', answer: 'Oui, chaque artisan sur AfficheServices est vérifié : SIRET valide, assurance RC Pro, identité contrôlée. Les avis clients sont authentiques et modérés.' },
  { question: 'Le service est-il gratuit ?', answer: 'Oui, AfficheServices est 100% gratuit pour les particuliers. Vous pouvez comparer les devis sans aucun engagement.' },
]

// ─── Loader ──────────────────────────────────────────────────

export async function loader({ params }: LoaderFunctionArgs) {
  const { category: catSlug, city: citySlug } = params

  // Validate slugs against the canonical pattern (lowercase a-z, digits,
  // single dashes). Without this, malformed URLs like /services/foo/💩 or
  // /services/foo/A%20BC%23 produce garbage cityName strings that get
  // splashed across the H1, meta tags, and DB ilike pattern (audit #22).
  const SLUG_RE = /^[a-z0-9]+(?:-[a-z0-9]+)*$/
  if (!citySlug || !SLUG_RE.test(citySlug) || citySlug.length > 64) {
    throw new Response('Ville non trouvée', { status: 404 })
  }
  if (!catSlug || !SLUG_RE.test(catSlug) || catSlug.length > 64) {
    throw new Response('Catégorie non trouvée', { status: 404 })
  }

  // DOM-TOM not served — 410 before any expensive work.
  await assertCityNotDomTom(citySlug)

  const cityName = formatCityName(citySlug)
  const cityLower = cityName.toLowerCase()

  // Stage 1 — independent queries, no cat.id dependency. Parallelizing
  // these from a 13-query serial chain (12s TTFB) to two parallel stages
  // (target: <2s TTFB). All ILIKE matches rewritten to LOWER() = LOWER()
  // so the new functional indexes get hit (migration 0012).
  const [
    catRows,
    relatedCities,
    relatedCategories,
    geoRowRows,
    aiContent,
    cityContextRow,
  ] = await Promise.all([
    db.select().from(serviceCategories).where(eq(serviceCategories.slug, catSlug ?? '')).limit(1),
    db.select({ name: geoFrance.communeName })
      .from(geoFrance)
      .where(sql`${geoFrance.communeName} != ${cityName}`)
      .orderBy(sql`RANDOM()`).limit(8),
    db.select({ name: serviceCategories.nameFr, slug: serviceCategories.slug })
      .from(serviceCategories)
      .where(sql`${serviceCategories.parentId} IS NULL AND ${serviceCategories.slug} != ${catSlug}`)
      .orderBy(sql`RANDOM()`).limit(5),
    db.select({
        communeName: geoFrance.communeName,
        postalCode: geoFrance.postalCode,
        departmentCode: geoFrance.departmentCode,
        departmentName: geoFrance.departmentName,
        regionName: geoFrance.regionName,
        population: geoFrance.population,
        latitude: geoFrance.latitude,
        longitude: geoFrance.longitude,
        communeCode: geoFrance.communeCode,
      })
      .from(geoFrance)
      .where(sql`LOWER(${geoFrance.communeName}) = ${cityLower}`)
      .limit(1),
    getSeoContent(catSlug ?? '', cityName),
    fetchCityContext(cityName),
  ])
  const cat = catRows[0]
  if (!cat) {
    // Per GSC 2026-05-30 audit, invalid /services/[cat]/[city] URLs surface
    // in "Not found (404)" bucket (~50 sample pages). Redirect to /services
    // hub so backlink equity + crawl budget flow to the indexable surface
    // instead of throwing 404. Permanent (301) signals the change is final.
    throw redirect('/services', 301)
  }
  const geoRow = geoRowRows[0]

  // Stage 2 — depends on cat (and optionally geoRow). All parallel.
  const cityPredicate = sql`LOWER(${professionalProfiles.city}) = ${cityLower}`
  const proSelect = {
    id: professionalProfiles.id,
    companyName: professionalProfiles.companyName,
    slug: professionalProfiles.slug,
    city: professionalProfiles.city,
    departmentCode: professionalProfiles.departmentCode,
    ratingAvg: professionalProfiles.ratingAvg,
    reviewCount: professionalProfiles.reviewCount,
    responseTimeAvgHours: professionalProfiles.responseTimeAvgHours,
    identityVerified: professionalProfiles.identityVerified,
    insuranceVerified: professionalProfiles.insuranceVerified,
    shortDescription: professionalProfiles.shortDescription,
    yearsExperience: professionalProfiles.yearsExperience,
    latitude: professionalProfiles.latitude,
    longitude: professionalProfiles.longitude,
  } as const

  const [subs, pros, cityStatsRows, nearbyCities] = await Promise.all([
    db.select({ id: serviceCategories.id, name: serviceCategories.nameFr, slug: serviceCategories.slug })
      .from(serviceCategories)
      .where(eq(serviceCategories.parentId, cat.id)),
    db.select(proSelect)
      .from(professionalProfiles)
      .innerJoin(professionalCategories, eq(professionalCategories.professionalId, professionalProfiles.id))
      .where(and(
        eq(professionalCategories.categoryId, cat.id),
        eq(professionalProfiles.identityVerified, true),
        cityPredicate,
      ))
      .orderBy(desc(professionalProfiles.ratingAvg))
      .limit(PAGE_SIZE),
    db.select({
        avgRating: sql<string>`ROUND(AVG(${professionalProfiles.ratingAvg}::numeric), 1)`,
        totalReviews: sql<number>`COALESCE(SUM(${professionalProfiles.reviewCount}), 0)::int`,
        avgResponseHours: sql<number>`COALESCE(AVG(${professionalProfiles.responseTimeAvgHours}), 0)::int`,
        avgYearsExp: sql<number>`COALESCE(AVG(${professionalProfiles.yearsExperience}), 0)::int`,
        verifiedCount: sql<number>`COUNT(*) FILTER (WHERE ${professionalProfiles.insuranceVerified} = true)::int`,
        totalInCity: sql<number>`COUNT(*)::int`,
      })
      .from(professionalProfiles)
      .innerJoin(professionalCategories, eq(professionalCategories.professionalId, professionalProfiles.id))
      .where(and(
        eq(professionalCategories.categoryId, cat.id),
        eq(professionalProfiles.identityVerified, true),
        geoRow
          ? eq(professionalProfiles.departmentCode, geoRow.departmentCode)
          : cityPredicate,
      )),
    geoRow
      ? db.select({ name: geoFrance.communeName, population: geoFrance.population })
          .from(geoFrance)
          .where(and(
            eq(geoFrance.departmentCode, geoRow.departmentCode),
            sql`${geoFrance.communeName} != ${cityName}`,
          ))
          .orderBy(sql`${geoFrance.population} DESC NULLS LAST`)
          .limit(6)
      : Promise.resolve([] as Array<{ name: string; population: number | null }>),
  ])
  const cityStats = cityStatsRows[0]

  // Stage 3 — broaden to department if city result thin. Rare path,
  // accepted as serial.
  let allPros = pros
  if (pros.length < 3 && geoRow) {
    allPros = await db.select(proSelect)
      .from(professionalProfiles)
      .innerJoin(professionalCategories, eq(professionalCategories.professionalId, professionalProfiles.id))
      .where(and(
        eq(professionalCategories.categoryId, cat.id),
        eq(professionalProfiles.identityVerified, true),
        eq(professionalProfiles.departmentCode, geoRow.departmentCode),
      ))
      .orderBy(desc(professionalProfiles.ratingAvg))
      .limit(PAGE_SIZE)
  }

  const faqs = SERVICE_FAQS[catSlug ?? ''] ?? DEFAULT_FAQS
  const cityContent = generateCityServiceContent(catSlug ?? '', cityName, cityContextRow, {
    // DB-driven category name beats SERVICE_LABELS for sub-categories
    // ('plomberie-fuite-d-eau' has no SL entry; admin-edited nameFr does).
    displayName: cat.nameFr,
  })

  return {
    category: cat,
    cityName,
    citySlug: citySlug ?? '',
    subcategories: subs,
    pros: allPros,
    relatedCities: relatedCities.map((c) => c.name),
    relatedCategories,
    faqs,
    cityContent,
    aiContent,
    geo: geoRow ?? null,
    wikidataUrl: wikidataUrlForInsee(geoRow?.communeCode ?? null),
    communeImageUrl: communeImageForInsee(geoRow?.communeCode ?? null),
    nearbyCities,
    // Reciprocal link to the price guide (/prix) for the same (service, city) —
    // complementary-pair signal so the two URLs don't read as duplicates.
    prixUrl: `/prix/${artisanSlug(cat.slug)}-${citySlug ?? ''}`,
    dbStats: {
      proCount: allPros.length,
      avgRating: cityStats?.avgRating ?? null,
      totalReviews: cityStats?.totalReviews ?? 0,
      avgResponseHours: cityStats?.avgResponseHours ?? 0,
      avgYearsExp: cityStats?.avgYearsExp ?? 0,
      verifiedCount: cityStats?.verifiedCount ?? 0,
      totalInDepartment: cityStats?.totalInCity ?? 0,
    },
  }
}

export function headers() { return SEO_PAGE_HEADERS }

export const meta: MetaFunction<typeof loader> = ({ data }) => {
  // Stade-2 DB overrides win over the template title/description when authored.
  const title = data?.aiContent?.metaTitle ?? data?.cityContent?.metaTitle ?? 'Service | AfficheServices'
  const description = data?.aiContent?.metaDescription ?? data?.cityContent?.metaDescription
  const tags: ReturnType<MetaFunction> = [
    { title },
    { name: 'description', content: description },
    { tagName: 'link', rel: 'canonical', href: `https://www.afficheservices.fr/services/${data?.category?.slug}/${data?.citySlug}` },
  ]
  // Gate index on REAL DB seo_content (aiContent), not the always-present
  // template `cityContent` — the old check was true for every page, so all
  // ~40k thin (service,city) URLs were index,follow (scaled-content risk +
  // "Crawled - currently not indexed"). aiContent is the same signal the
  // sitemap uses (fetchIndexableContentLastmod), so sitemap ⇔ index now agree.
  const hasRichContent = Boolean(data?.aiContent)
  tags.push({ name: 'robots', content: hasRichContent ? 'index, follow' : 'noindex, follow' })

  if (title) tags.push({ property: 'og:title', content: title })
  if (description) tags.push({ property: 'og:description', content: description })
  // og:locale + geo meta — strengthens "Sites de lieux" signal
  tags.push({ property: 'og:locale', content: 'fr_FR' })
  tags.push({ property: 'og:type', content: 'website' })
  // Complete the social card: url + image + twitter pair (were missing).
  const canonical = `https://www.afficheservices.fr/services/${data?.category?.slug}/${data?.citySlug}`
  const socialTitle = title ?? `${data?.category?.nameFr ?? 'Service'} à ${data?.cityName ?? ''}`
  const socialDesc = description ?? ''
  tags.push(
    { property: 'og:url', content: canonical },
    { property: 'og:image', content: `https://www.afficheservices.fr/api/og?service=${encodeURIComponent(data?.category?.slug ?? '')}&subtitle=${encodeURIComponent(data?.cityName ?? '')}` },
    { property: 'og:image:width', content: '1200' },
    { property: 'og:image:height', content: '630' },
    { name: 'twitter:title', content: socialTitle },
    { name: 'twitter:description', content: socialDesc },
  )
  if (data?.geo) {
    tags.push(...geoMeta({
      region: data.geo.departmentCode,
      placename: data.cityName ?? '',
      lat: data.geo.latitude ? parseFloat(String(data.geo.latitude)) : null,
      lon: data.geo.longitude ? parseFloat(String(data.geo.longitude)) : null,
    }))
  }
  return tags
}

// ─── Component ───────────────────────────────────────────────

export default function CityServicePage() {
  const { category, cityName, citySlug, subcategories, pros, relatedCities, relatedCategories, faqs, cityContent, aiContent, dbStats, geo, nearbyCities, wikidataUrl, communeImageUrl, prixUrl } = useLoaderData<typeof loader>()
  // Artisan noun ("plombier", "électricien"…) for grammatical phrasing — the
  // category nameFr ("plomberie") is a trade, not a person: "un plomberie" is
  // wrong. Falls back to the trade name for sub-categories without a label.
  const artisanNoun = SERVICE_LABELS[category.slug]?.artisan ?? category.nameFr.toLowerCase()

  return (
    <div className="min-h-screen bg-surface-secondary">
      <Navbar />

      {/* Structured Data */}
      <ServiceSchema category={category} />
      {geo && (() => {
        // Aggregate rating across local pros — drives "★ N (M avis)" rich snippet
        const rated = (pros ?? []).filter((p) => p.ratingAvg && parseFloat(p.ratingAvg) > 0 && p.reviewCount > 0)
        const totalReviews = rated.reduce((sum, p) => sum + p.reviewCount, 0)
        const weighted = rated.reduce((sum, p) => sum + parseFloat(p.ratingAvg!) * p.reviewCount, 0)
        const aggregateRating = totalReviews > 0
          ? { value: Math.round((weighted / totalReviews) * 10) / 10, count: totalReviews }
          : null
        return (
          <CityPlaceSchema
            city={{
              name: cityName ?? '',
              postalCode: geo.postalCode ?? '',
              dept: geo.departmentCode ?? '',
              deptName: geo.departmentName ?? '',
              region: geo.regionName ?? '',
              lat: geo.latitude ?? 0,
              lon: geo.longitude ?? 0,
              population: geo.population ?? null,
              communeCode: geo.communeCode ?? null,
            }}
            service={{ name: category.nameFr, slug: category.slug }}
            canonicalUrl={`https://www.afficheservices.fr/services/${category.slug}/${citySlug}`}
            aggregateRating={aggregateRating}
            wikidataUrl={wikidataUrl}
            communeImageUrl={communeImageUrl}
          />
        )
      })()}
      <ItemListSchema
        name={`${category.nameFr} à ${cityName}`}
        description={`Liste des ${category.nameFr.toLowerCase()} vérifiés à ${cityName}`}
        url={`https://www.afficheservices.fr/services/${category.slug}/${citySlug}`}
        items={(pros ?? []).slice(0, 10).map((p) => ({
          name: p.companyName,
          url: `https://www.afficheservices.fr/pro/${p.slug}`,
          rating: p.ratingAvg ? parseFloat(p.ratingAvg) : null,
          reviewCount: p.reviewCount,
          image: null,
          city: p.city,
        }))}
      />
      <FAQSchema items={faqs} />
      <BreadcrumbSchema items={[
        { name: 'Accueil', url: 'https://www.afficheservices.fr/' },
        { name: 'Services', url: 'https://www.afficheservices.fr/services' },
        { name: category.nameFr, url: `https://www.afficheservices.fr/services/${category.slug}` },
        { name: cityName, url: `https://www.afficheservices.fr/services/${category.slug}/${citySlug}` },
      ]} />

      {/* Breadcrumb */}
      <div className="border-b border-border bg-white">
        <div className="mx-auto max-w-7xl px-4 py-2.5 lg:px-6">
          <nav className="flex items-center gap-2 text-xs text-slate-500">
            <Link to="/" className="hover:text-primary">Accueil</Link>
            <span>/</span>
            <Link to="/services" className="hover:text-primary">Services</Link>
            <span>/</span>
            <Link to={`/services/${category.slug}`} className="hover:text-primary">{category.nameFr}</Link>
            <span>/</span>
            <span className="text-slate-900">{cityName}</span>
          </nav>
        </div>
      </div>

      {/* Article + HowTo schemas (alongside other SSR schemas) */}
      <ArticleSchema
        title={cityContent.metaTitle}
        description={cityContent.metaDescription}
        url={`https://www.afficheservices.fr/services/${category.slug}/${citySlug}`}
      />
      <HowToSchema
        name={`Comment obtenir un devis ${category.nameFr.toLowerCase()} à ${cityName}`}
        steps={cityContent.howItWorks}
      />

      <div className="mx-auto max-w-7xl px-4 py-8 lg:px-6">

        {/* ══ HERO ══ */}
        <div className="rounded-2xl bg-gradient-to-br from-slate-900 to-slate-800 p-8 text-white sm:p-10">
          <h1 className="text-2xl font-bold sm:text-3xl">{cityContent.heroTitle}</h1>
          <p className="mt-2 text-sm text-slate-300">{cityContent.heroSubtitle}</p>
          {/* Answer-first block (Stade-2) — concise, citable by AI Overviews. */}
          {aiContent?.answerBlock && (
            <p className="mt-3 max-w-2xl text-sm leading-relaxed text-slate-200">{aiContent.answerBlock}</p>
          )}
          <div className="mt-4 flex flex-wrap gap-3">
            <Link to="/demande-devis/1" className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-white shadow-md hover:bg-primary-hover btn-press">
              Devis gratuit <ArrowRight className="h-4 w-4" />
            </Link>
            <Link to={prixUrl} className="inline-flex items-center gap-1.5 rounded-xl border border-white/20 px-4 py-3 text-sm font-medium text-slate-200 hover:bg-white/10">
              Voir les prix {category.nameFr.toLowerCase()} à {cityName}
            </Link>
          </div>
          <span className="mt-2 flex items-center gap-1.5 text-xs text-slate-400">
            <CheckCircle className="h-3.5 w-3.5 text-emerald-400" /> 100% gratuit · Sans engagement · 48h
          </span>
        </div>

        {/* Article meta + real stats */}
        <div className="mt-4 space-y-3">
          <ArticleMeta readingTime={Math.max(5, Math.round(cityContent.tips.length * 1.2))} />
          <PageStats proCount={dbStats.proCount} avgRating={dbStats.avgRating} reviewCount={dbStats.totalReviews} cityName={cityName} />
        </div>

        {/* ══ Real per-city DB data (uniqueness driver) ══ */}
        {geo && (
          <section id="ville-contexte" className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <h2 className="text-lg font-semibold text-slate-900">
              {dbStats.totalInDepartment > 0
                ? `${category.nameFr} à ${cityName} — le marché local en chiffres`
                : `${cityName} (${geo.departmentCode}) — repères locaux pour votre projet`}
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              {dbStats.totalInDepartment > 0 ? (
                <>
                  Données agrégées depuis notre base sur les {dbStats.totalInDepartment} artisans{' '}
                  {category.nameFr.toLowerCase()} actifs dans le {geo.departmentName} ({geo.departmentCode}).
                </>
              ) : (
                <>
                  Décrivez votre projet de {category.nameFr.toLowerCase()} à {cityName} : nous transmettons votre
                  demande à des artisans du {geo.departmentName} ({geo.departmentCode}), tous vérifiés (SIRET, RC Pro,
                  garantie décennale) avant toute mise en relation.
                </>
              )}
            </p>
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
              <div className="rounded-xl bg-slate-50 p-3">
                <div className="text-xs text-slate-500">Population · {cityName}</div>
                <div className="mt-1 font-mono text-sm font-semibold text-slate-900">
                  {geo.population ? geo.population.toLocaleString('fr-FR') + ' hab.' : '—'}
                </div>
              </div>
              <div className="rounded-xl bg-slate-50 p-3">
                <div className="text-xs text-slate-500">Code postal</div>
                <div className="mt-1 font-mono text-sm font-semibold text-slate-900">{geo.postalCode}</div>
              </div>
              {dbStats.totalInDepartment > 0 && (
                <>
                  <div className="rounded-xl bg-slate-50 p-3">
                    <div className="text-xs text-slate-500">Artisans vérifiés (dépt.)</div>
                    <div className="mt-1 font-mono text-sm font-semibold text-slate-900">{dbStats.verifiedCount}</div>
                  </div>
                  {dbStats.avgResponseHours > 0 && (
                    <div className="rounded-xl bg-slate-50 p-3">
                      <div className="text-xs text-slate-500">Temps de réponse moyen</div>
                      <div className="mt-1 font-mono text-sm font-semibold text-slate-900">{dbStats.avgResponseHours} h</div>
                    </div>
                  )}
                  {dbStats.avgYearsExp > 0 && (
                    <div className="rounded-xl bg-slate-50 p-3">
                      <div className="text-xs text-slate-500">Expérience moyenne</div>
                      <div className="mt-1 font-mono text-sm font-semibold text-slate-900">{dbStats.avgYearsExp} ans</div>
                    </div>
                  )}
                </>
              )}
              <div className="rounded-xl bg-slate-50 p-3">
                <div className="text-xs text-slate-500">Région</div>
                <div className="mt-1 text-sm font-semibold text-slate-900">{geo.regionName}</div>
              </div>
              <div className="rounded-xl bg-slate-50 p-3 sm:col-span-2">
                <div className="text-xs text-slate-500">Coordonnées</div>
                <div className="mt-1 font-mono text-xs text-slate-900">
                  {parseFloat(String(geo.latitude)).toFixed(4)}° N, {parseFloat(String(geo.longitude)).toFixed(4)}° E
                </div>
              </div>
            </div>
            {nearbyCities.length > 0 && (
              <div className="mt-4 border-t border-slate-100 pt-4">
                <div className="text-xs uppercase tracking-wide text-slate-500">
                  Communes voisines dans le {geo.departmentName}
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {nearbyCities.map((c) => {
                    const slug = c.name.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '')
                    return (
                      <Link
                        key={c.name}
                        to={`/services/${category.slug}/${slug}`}
                        className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-700 hover:border-primary hover:text-primary"
                      >
                        {c.name}
                        {c.population ? <span className="ml-1 text-slate-400">· {c.population.toLocaleString('fr-FR')}</span> : null}
                      </Link>
                    )
                  })}
                </div>
              </div>
            )}
          </section>
        )}

        {/* Table of Contents */}
        <div className="mt-6">
          <TableOfContents items={[
            { id: 'introduction', label: `${category.nameFr} à ${cityName} — Introduction` },
            { id: 'prix', label: 'Prix et tarifs détaillés' },
            { id: 'facteurs', label: 'Ce qui influence le prix' },
            { id: 'economies', label: 'Comment économiser' },
            { id: 'pourquoi', label: 'Pourquoi AfficheServices' },
            { id: 'etapes', label: 'Comment obtenir un devis' },
            { id: 'conseils', label: 'Conseils pratiques' },
            { id: 'comparaison', label: 'Avec vs sans plateforme' },
            { id: 'infos', label: 'Informations locales' },
            { id: 'faq', label: 'Questions fréquentes' },
          ]} />
        </div>

        {/* ══ INTRODUCTION (3 paragraphs) ══ */}
        <article className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-xs sm:p-8">
          <h2 className="text-lg font-bold text-slate-900"><span id="introduction" />Trouver un {artisanNoun} de confiance à {cityName}</h2>
          <div className="mt-4 space-y-4 text-sm leading-relaxed text-slate-600">
            {cityContent.introParas.map((para, i) => (
              <p key={i}>{para}</p>
            ))}
          </div>
        </article>

        {/* ══ DEMAND COUNTER ══ */}
        <div className="mt-6">
          <DemandCounter cityName={cityName} serviceName={category.nameFr} variant="full" />
        </div>

        {/* ══ INLINE QUIZ ══ */}
        <div className="mt-6">
          <InlineQuiz serviceName={category.nameFr} cityName={cityName} categorySlug={category.slug} />
        </div>

        {/* ══ PRIX DÉTAILLÉS ══ */}
        <div className="mt-8 rounded-2xl border border-border bg-white shadow-xs overflow-hidden">
          <div className="border-b border-border px-6 py-4 sm:px-8">
            <h2 className="text-lg font-bold text-slate-900"><span id="prix" />Prix {category.nameFr.toLowerCase()} à {cityName} — Tarifs 2026</h2>
            <p className="mt-1 text-sm text-slate-500">{cityContent.pricingIntro}</p>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border bg-slate-50 text-left text-xs font-semibold uppercase tracking-wider text-slate-500">
                  <th className="px-6 py-3">Prestation</th>
                  <th className="px-6 py-3 text-right">Prix min</th>
                  <th className="px-6 py-3 text-right">Prix max</th>
                  <th className="hidden px-6 py-3 sm:table-cell">Détail</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {cityContent.pricing.rows.map((row, i) => (
                  <tr key={i} className="hover:bg-slate-50">
                    <td className="px-6 py-3.5 text-sm font-medium text-slate-900">{row.service}</td>
                    <td className="px-6 py-3.5 text-right text-sm font-semibold text-slate-900">{row.priceMin} €</td>
                    <td className="px-6 py-3.5 text-right text-sm font-semibold text-slate-900">{row.priceMax} €</td>
                    <td className="hidden px-6 py-3.5 text-xs text-slate-500 sm:table-cell">{row.detail}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="border-t border-border bg-slate-50 px-6 py-3 text-xs text-slate-400">
            * Prix indicatifs TTC à {cityName}, mars 2026. Varient selon complexité et accessibilité.
          </div>
        </div>

        {/* ══ FACTEURS DE PRIX ══ */}
        <div className="mt-6 rounded-2xl border border-border bg-white p-6 shadow-xs">
          <h3 className="text-base font-bold text-slate-900"><span id="facteurs" />Ce qui influence le prix à {cityName}</h3>
          <ul className="mt-4 space-y-2.5">
            {cityContent.pricing.factors.map((factor, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-slate-600">
                <span className="mt-1 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-100 text-[10px] font-bold text-amber-700">{i + 1}</span>
                {factor}
              </li>
            ))}
          </ul>
        </div>

        {/* ══ COMMENT ÉCONOMISER ══ */}
        <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-6">
          <h3 className="text-base font-bold text-emerald-900"><span id="economies" />Comment économiser sur vos travaux à {cityName}</h3>
          <ul className="mt-3 space-y-2">
            {cityContent.pricing.savings.map((tip, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-emerald-800">
                <CheckCircle className="mt-0.5 h-4 w-4 shrink-0 text-emerald-500" />
                {tip}
              </li>
            ))}
          </ul>
        </div>

        {/* ══ POURQUOI NOUS CHOISIR ══ */}
        <div className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-xs sm:p-8">
          <h2 className="text-lg font-bold text-slate-900"><span id="pourquoi" />Pourquoi choisir AfficheServices à {cityName} ?</h2>
          <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {cityContent.whyPoints.map((point, i) => (
              <div key={i} className="rounded-xl bg-slate-50 p-4">
                <h4 className="text-sm font-semibold text-slate-900">{point.title}</h4>
                <p className="mt-1 text-xs leading-relaxed text-slate-500">{point.text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ══ COMMENT ÇA MARCHE ══ */}
        <div className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-xs sm:p-8">
          <h2 className="text-lg font-bold text-slate-900"><span id="etapes" />Comment obtenir un devis à {cityName} ?</h2>
          <div className="mt-5 space-y-5">
            {cityContent.howItWorks.map((step) => (
              <div key={step.step} className="flex gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-bold text-white">{step.step}</div>
                <div>
                  <h4 className="text-sm font-semibold text-slate-900">{step.title}</h4>
                  <p className="mt-0.5 text-xs text-slate-500">{step.detail}</p>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* ══ CONSEILS DÉTAILLÉS ══ */}
        <div className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-xs sm:p-8">
          <h2 className="text-lg font-bold text-slate-900"><span id="conseils" />Nos conseils pour vos travaux de {category.nameFr.toLowerCase()} à {cityName}</h2>
          <div className="mt-5 space-y-4">
            {cityContent.tips.map((tip, i) => (
              <div key={i} className="rounded-xl bg-slate-50 p-4">
                <h4 className="text-sm font-semibold text-slate-900">{tip.title}</h4>
                <p className="mt-1 text-xs leading-relaxed text-slate-500">{tip.text}</p>
              </div>
            ))}
          </div>
        </div>

        {/* ══ INFO LOCALE ══ */}
        <div className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-xs">
          <h2 className="text-lg font-bold text-slate-900"><span id="infos" />{category.nameFr} à {cityName} — Informations locales</h2>
          <div className="mt-4 space-y-3">
            {cityContent.localInfo.map((info, i) => (
              <p key={i} className="text-sm leading-relaxed text-slate-600">{info.replace(/\*\*/g, '')}</p>
            ))}
          </div>
        </div>

        {/* Subcategories */}
        {subcategories.length > 0 && (
          <div className="mt-4 flex flex-wrap gap-2">
            {subcategories.map((sub) => (
              <Link
                key={sub.id}
                to={`/services/${sub.slug}`}
                className="rounded-full border border-border bg-white px-3 py-1.5 text-xs font-medium text-slate-600 shadow-xs hover:border-primary hover:text-primary"
              >
                {sub.name}
              </Link>
            ))}
          </div>
        )}

        {/* Map */}
        {pros.length > 0 && (
          <ProMap
            pros={pros}
            centerLat={parseFloat(pros[0]?.latitude ?? '48.8566')}
            centerLng={parseFloat(pros[0]?.longitude ?? '2.3522')}
            className="mt-6"
          />
        )}

        {/* CTA */}
        <div className="mt-6 flex items-center gap-3">
          <Link
            to="/demande-devis/1"
            className="inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-white shadow-sm hover:bg-primary-hover btn-press"
          >
            Devis gratuit à {cityName} <ArrowRight className="h-4 w-4" />
          </Link>
          <span className="text-xs text-slate-400">100% gratuit · Sans engagement</span>
        </div>

        {/* Tips section */}
        <div className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-xs">
          <h2 className="text-lg font-bold text-slate-900">Conseils pratiques</h2>
          <ul className="mt-4 space-y-3">
            {cityContent.tips.map((tip, i) => (
              <li key={i} className="flex items-start gap-3 text-sm text-slate-600">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-amber-100 text-[10px] font-bold text-amber-700">{i + 1}</span>
                <div><span className="font-semibold text-slate-800">{tip.title}</span> — {tip.text}</div>
              </li>
            ))}
          </ul>
        </div>

        {/* Pro list (if any) */}
        <div className="mt-8 space-y-3">
          {pros.length === 0 ? (
            <div className="rounded-2xl border border-border bg-white p-8 text-center shadow-xs">
              <MapPin className="mx-auto h-10 w-10 text-primary/40" />
              <h2 className="mt-3 text-base font-semibold text-slate-900">
                Des {artisanNoun}s arrivent bientôt à {cityName}
              </h2>
              <p className="mt-1 text-sm text-slate-500">
                En attendant, décrivez votre projet — nous vous mettrons en relation avec les pros les plus proches
              </p>
              <Link to="/demande-devis/1" className="mt-4 inline-flex items-center gap-2 rounded-xl bg-primary px-6 py-3 text-sm font-semibold text-white hover:bg-primary-hover btn-press">
                Demander un devis gratuit <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          ) : (
            pros.map((pro) => (
              <ProCard key={pro.id} pro={pro} density="compact" />
            ))
          )}
        </div>

        {/* ══ CITY PRICE COMPARISON ══ */}
        <div className="mt-8">
          <CityPriceComparison serviceSlug={category.slug} currentCity={cityName} />
        </div>

        {/* ══ AI EXPERT CONTENT ══ */}
        {aiContent && (
          <>
            <article className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-xs">
              <ContentMeta cityName={cityName} serviceName={category.nameFr} />
              <h2 className="mt-3 text-lg font-bold text-slate-900">{category.nameFr} à {cityName} — guide expert</h2>
              <div className="mt-4 space-y-3 text-sm leading-relaxed text-slate-600">
                {aiContent.expertIntroParas.map((p, i) => <p key={i}>{p}</p>)}
              </div>
              <SourceCitations serviceSlug={category.slug} />
            </article>
            {aiContent.localContextParas.length > 0 && (
              <div className="mt-6 rounded-2xl border border-blue-100 bg-blue-50 p-6">
                <h3 className="text-base font-bold text-blue-900">Spécificités locales — {cityName}</h3>
                <div className="mt-3 space-y-2 text-sm leading-relaxed text-blue-800">
                  {aiContent.localContextParas.map((p, i) => <p key={i}>{p}</p>)}
                </div>
              </div>
            )}
            {aiContent.priceAnalysis && (
              <div className="mt-6 rounded-2xl border border-emerald-200 bg-emerald-50 p-6">
                <h3 className="text-base font-bold text-emerald-900">Analyse des prix — {cityName}</h3>
                <p className="mt-3 text-sm leading-relaxed text-emerald-800">{aiContent.priceAnalysis}</p>
              </div>
            )}
            <InternaunteQuestions serviceSlug={category.slug} cityName={cityName} artisan={artisanNoun} />
          </>
        )}

        {/* ══ COMPARISON TABLE ══ */}
        <div className="mt-8" id="comparaison">
          <ComparisonTable serviceName={category.nameFr} />
        </div>

        {/* FAQ Section */}
        <div className="mt-8 rounded-2xl border border-border bg-white p-6 shadow-xs" id="faq">
          <h2 className="text-lg font-bold text-slate-900">
            Questions fréquentes — {category.nameFr} à {cityName}
          </h2>
          <div className="mt-4 space-y-4">
            {(aiContent?.faqItems ?? faqs.map(f => ({ q: f.question, a: f.answer }))).map((faq, i) => (
              <div key={i} className="rounded-xl border border-slate-200 bg-white p-5">
                <h3 className="text-sm font-semibold text-slate-900">{faq.q}</h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">{faq.a}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Internal links — SEO juice distribution */}
        <div className="mt-8 grid gap-6 sm:grid-cols-2">
          {/* Related cities */}
          <div className="rounded-2xl border border-border bg-white p-5 shadow-xs">
            <h3 className="text-sm font-semibold text-slate-900">
              {category.nameFr} dans d'autres villes
            </h3>
            <div className="mt-3 flex flex-wrap gap-2">
              {relatedCities.map((city) => (
                <Link
                  key={city}
                  to={`/services/${category.slug}/${city.toLowerCase().replace(/\s+/g, '-').replace(/'/g, '-')}`}
                  className="rounded-full border border-border px-3 py-1.5 text-xs text-slate-600 hover:border-primary hover:text-primary"
                >
                  {category.nameFr} {city}
                </Link>
              ))}
            </div>
          </div>

          {/* Related services */}
          <div className="rounded-2xl border border-border bg-white p-5 shadow-xs">
            <h3 className="text-sm font-semibold text-slate-900">
              Autres services à {cityName}
            </h3>
            <div className="mt-3 flex flex-wrap gap-2">
              {relatedCategories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/services/${cat.slug}/${citySlug}`}
                  className="rounded-full border border-border px-3 py-1.5 text-xs text-slate-600 hover:border-primary hover:text-primary"
                >
                  {cat.name} {cityName}
                </Link>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-8 rounded-2xl bg-surface-dark p-8 text-center sm:p-10">
          <h2 className="text-xl font-bold text-white">
            Besoin d'un {artisanNoun} à {cityName} ?
          </h2>
          <p className="mt-2 text-sm text-slate-400">
            Recevez jusqu'à 3 devis gratuits de professionnels vérifiés
          </p>
          <Link
            to="/demande-devis/1"
            className="mt-5 inline-flex items-center gap-2 rounded-xl bg-primary px-8 py-3 text-sm font-semibold text-white shadow-md hover:bg-primary-hover btn-press"
          >
            Demander un devis gratuit <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>

      <FloatingCTA
        href="/demande-devis/1"
        label={`Devis ${category.nameFr.toLowerCase()} à ${cityName}`}
        subtitle="3 pros vérifiés · sous 48h"
      />

      <Footer />
    </div>
  )
}
